import { useState, useEffect, useCallback, useRef } from "react";
import localforage from "localforage";

localforage.config({
  name: "EmployeeReportsCRM",
  storeName: "offline_queue",
});

/**
 * @param {string | null | undefined} username
 * @returns {string | null} localforage item key for this user's queue
 */
export function offlineQueueKeyForUser(username) {
  const safe = String(username || "").trim();
  if (!safe) return null;
  return `technician_offline_queue_${encodeURIComponent(safe)}`;
}

/** Stable key for a queued file entry (IndexedDB row). */
export function offlineQueuedFileStableKey(entry) {
  if (!entry) return "";
  const len = entry.data?.byteLength ?? 0;
  return `${entry.name || ""}|${entry.lastModified ?? 0}|${len}`;
}

/** Stable key for a live File (must match offlineQueuedFileStableKey for the same logical file). */
export function offlineFileStableKey(file) {
  if (!file) return "";
  return `${file.name}|${file.lastModified}|${file.size}`;
}

function progressStorageKey(queueKey, itemId) {
  return `${queueKey}::progress::${itemId}`;
}

async function readQueue(queueKey) {
  const q = await localforage.getItem(queueKey);
  return Array.isArray(q) ? q : [];
}

function isQuotaError(e) {
  return (
    e &&
    (e.name === "QuotaExceededError" ||
      e.code === 22 ||
      e.code === "QuotaExceededError")
  );
}

async function writeQueue(queueKey, queue) {
  try {
    await localforage.setItem(queueKey, queue);
  } catch (e) {
    if (isQuotaError(e)) {
      const err = new Error(
        "Δεν υπάρχει αρκετός χώρος στον browser για αποθήκευση εκτός σύνδεσης. Μείνετε συνδεδεμένοι για μεγάλα αρχεία ή ελευθερώστε χώρο."
      );
      err.cause = e;
      err.code = "OFFLINE_QUOTA_EXCEEDED";
      throw err;
    }
    throw e;
  }
}

export async function readOfflineUploadProgress(queueKey, itemId) {
  if (!queueKey || !itemId) return null;
  const raw = await localforage.getItem(progressStorageKey(queueKey, itemId));
  return raw && typeof raw === "object" ? raw : null;
}

export async function writeOfflineUploadProgress(queueKey, itemId, data) {
  if (!queueKey || !itemId) return;
  try {
    await localforage.setItem(progressStorageKey(queueKey, itemId), data);
  } catch (e) {
    if (isQuotaError(e)) {
      console.warn("[offline-sync] Progress write quota exceeded:", e);
      throw e;
    }
    throw e;
  }
}

export async function clearOfflineUploadProgress(queueKey, itemId) {
  if (!queueKey || !itemId) return;
  try {
    await localforage.removeItem(progressStorageKey(queueKey, itemId));
  } catch (e) {
    console.warn("[offline-sync] clearOfflineUploadProgress failed:", e);
  }
}

async function clearAllProgressForQueue(queueKey) {
  if (!queueKey) return;
  try {
    const keys = await localforage.keys();
    const suffix = `::progress::`;
    await Promise.all(
      keys
        .filter((k) => k.startsWith(queueKey + suffix))
        .map((k) => localforage.removeItem(k))
    );
  } catch (e) {
    console.warn("[offline-sync] clearAllProgressForQueue failed:", e);
  }
}

/**
 * Remove the offline queue for a given user (e.g. on logout).
 * @param {string | null | undefined} username
 */
export async function clearOfflineQueueForUser(username) {
  const queueKey = offlineQueueKeyForUser(username);
  if (!queueKey) return;
  try {
    await clearAllProgressForQueue(queueKey);
    await localforage.removeItem(queueKey);
  } catch (e) {
    console.warn("[offline-sync] clearOfflineQueueForUser failed:", e);
  }
}

/**
 * Persist pending technician submissions (fields + file buffers) and drain when online.
 * @param {object} options
 * @param {boolean} options.isOnline
 * @param {boolean} options.enabled
 * @param {string | null | undefined} options.queueUserKey — stable per-technician id (e.g. welcome name); required to read/write/drain
 * @param {(item: object, meta: object) => Promise<void>} options.processQueuedReport — meta has readProgress, writeProgress, clearProgress
 */
export function useOfflineSync({
  isOnline,
  enabled,
  queueUserKey,
  processQueuedReport,
}) {
  const [queueLength, setQueueLength] = useState(0);
  const processingRef = useRef(false);
  const processRef = useRef(processQueuedReport);
  processRef.current = processQueuedReport;

  const queueKey = offlineQueueKeyForUser(queueUserKey);

  const refreshLength = useCallback(async () => {
    if (!queueKey) {
      setQueueLength(0);
      return;
    }
    const q = await readQueue(queueKey);
    setQueueLength(q.length);
  }, [queueKey]);

  useEffect(() => {
    refreshLength();
  }, [refreshLength]);

  const enqueue = useCallback(
    async ({ fields, files }) => {
      if (!queueKey) {
        throw new Error("[offline-sync] Cannot enqueue without queueUserKey");
      }

      // Offline persistence must snapshot file bytes — there is no IndexedDB streaming API.
      // Online submissions use File + FormData (browser streams to the network).
      const fileEntries = await Promise.all(
        (files || []).map(async (f) => ({
          name: f.name,
          type: f.type || "application/octet-stream",
          lastModified: f.lastModified,
          data: await f.arrayBuffer(),
        }))
      );

      const item = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        createdAt: new Date().toISOString(),
        fields,
        files: fileEntries,
      };

      const q = await readQueue(queueKey);
      q.push(item);
      await writeQueue(queueKey, q);
      await refreshLength();
      return item.id;
    },
    [queueKey, refreshLength]
  );

  useEffect(() => {
    if (!enabled || !isOnline || !queueKey) {
      return undefined;
    }

    let cancelled = false;

    const drain = async () => {
      if (processingRef.current) return;
      processingRef.current = true;
      try {
        while (!cancelled) {
          const q = await readQueue(queueKey);
          if (q.length === 0) break;
          const [first, ...rest] = q;
          const meta = {
            queueKey,
            readProgress: () => readOfflineUploadProgress(queueKey, first.id),
            writeProgress: (data) => writeOfflineUploadProgress(queueKey, first.id, data),
            clearProgress: () => clearOfflineUploadProgress(queueKey, first.id),
          };
          try {
            await processRef.current(first, meta);
            if (cancelled) break;
            await writeQueue(queueKey, rest);
            await clearOfflineUploadProgress(queueKey, first.id);
            await refreshLength();
          } catch (e) {
            console.error("[offline-sync] Queue item failed, will retry later:", e);
            break;
          }
        }
      } finally {
        processingRef.current = false;
      }
    };

    drain();

    return () => {
      cancelled = true;
    };
  }, [isOnline, enabled, queueKey, refreshLength]);

  return { enqueue, queueLength, refreshLength, queueKey };
}

export function queuedFilesToFileObjects(fileEntries) {
  if (!Array.isArray(fileEntries)) return [];
  return fileEntries.map(
    (e) =>
      new File([new Uint8Array(e.data)], e.name, {
        type: e.type || "application/octet-stream",
        lastModified: e.lastModified || Date.now(),
      })
  );
}
